import {useEffect, useState, useRef, useMemo} from 'react';
import {Product} from '@amplify/athena-models';
import {makeStyles} from '@material-ui/core/styles';
import {savePrequalRecommendations} from './savePrequalRecommendations';
import {
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
    Button as MUIButton,
} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Spacing} from '../../Core/UI';
import ClientGoals from './Components/ClientGoals';
import ClientProgress from './Components/ClientProgress';
import BasicInfo from './Components/BasicInfo';
import Behavior from './Components/Behavior';
import Finances from './Components/Finances';
import HealthInfo from './Components/Health';
import {ClientQuoteData} from '../../APIs/CRM';
import {Parameter, ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import {Button} from '@/components/ui/button';
import {IS_PRODUCTION} from '../../constants';
import RootStore from '@/Core/mobx/RootStore';
import {SsnForm} from '@/APIs/types';
import {generatePath, useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import {SelectInputElement2} from './Components/SelectInputElement2';
import {RecommendedProduct} from '../Common/RecommendedProduct';
import CustomerPortalAPI, {Customer} from '@/APIs/CustomerPortalAPI';
import {BestFitProduct} from './Components/BestFitProduct';
import {productScriptPrimary, productScriptFallback, getProductFallbackTriggers} from './productConstants';
interface PrequalPanelParams {
    customerQuoteData?: ClientQuoteData[];
    valueChanged: (p?: Parameter, value?: string | number | null) => void;
    parameterStore: ParameterStore;
    contactsFormStore: typeof RootStore.agentPortalStore.contactsFormStore;
}

interface AllProductType extends Product {
    recommended?: boolean;
}

const useStyles = makeStyles({
    icon: {
        marginRight: 4,
    },
    approved: {
        color: 'green',
    },
    denied: {
        color: 'red',
    },
    sectionTitle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#E9ECFF',
        color: '#3F51B5',
        marginRight: 21,
        fontSize: 26,
        textAlign: 'center',
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
    recommendationTitle: {
        fontSize: 18,
        fontWeight: 500,
        color: '#838383',
    },
    qualifiedProductsContainer: {
        marginTop: 15,
        padding: '24px 12px 15px',
        border: '1px solid #E7E7E7',
        borderRadius: 6,
        color: '#838383',
    },
});

type QualifiedProductsType = {
    products: AllProductType[];
    productType: string;
};

function filterQualifiedProducts(products: AllProductType[]) {
    return products.filter(
        (product) =>
            !['AM', 'MOO'].includes(product.carrierCode) &&
            !['OMEXPRIUL', 'LSWIUL', 'LSWIULPRF', 'LSWTERM', 'LSWTERMPRF'].includes(product.code),
    );
}

function QualifiedProducts({products, productType}: QualifiedProductsType) {
    const classes = useStyles();
    const filteredProducts = filterQualifiedProducts(products);
    return (
        <Grid item xs={12} md={3}>
            <div style={{marginBottom: 8, fontWeight: 'bold'}}>{productType}</div>

            <Grid container item spacing={2}>
                {filteredProducts?.map((iul) => {
                    const icon = Object.keys(iul).includes('recommended') ? (
                        <span className={`${classes.icon}, ${classes.approved}`}>&#10003;</span>
                    ) : (
                        <span className={`${classes.icon}, ${classes.denied}`}>&#120;</span>
                    );

                    return (
                        <Grid item xs={12} key={`${iul.name}`}>
                            <span className={classes.icon}>{icon}</span>
                            {iul.name}
                        </Grid>
                    );
                })}
            </Grid>
        </Grid>
    );
}

interface MatchParams {
    tabIndex: string;
}

const FORM_TYPE_MAPPING: Record<string, 'prequal' | 'quote' | 'preapproval'> = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'preapproval',
};

let resolveConfirmSave: (confirm: boolean) => void;

export const PrequalPanel = observer(
    ({valueChanged, customerQuoteData, parameterStore, contactsFormStore}: PrequalPanelParams) => {
        const {prequal, health, walkthroughData} = parameterStore || {};
        const {historyFormId} = useParams<any>();

        const [loading, setLoading] = useState(false);
        const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
        const [duplicateSsnForms, setDuplicateSsnForms] = useState<SsnForm[]>([]);
        // const warningOffset = !RootStore.currentAgentStates.includes(parameterStore.approval.state.value) ? 80 : 0;

        const match = useRouteMatch<MatchParams>();
        const history = useHistory();

        const formType = FORM_TYPE_MAPPING[match.params.tabIndex];

        const confirmSave = async (): Promise<boolean> => {
            const response = await new Promise<boolean>((resolve) => {
                resolveConfirmSave = resolve;
                setSaveConfirmationOpen(true);
            });

            setSaveConfirmationOpen(false);

            return response;
        };

        const confirmPendingSave = () => {
            resolveConfirmSave(true);
        };

        const cancelPendingSave = () => {
            resolveConfirmSave(false);
        };

        const saveForm = async (selectedProduct?: string) => {
            setLoading(true);

            // Save the selected product if provided
            if (selectedProduct && prequal?.selectedProduct) {
                prequal.selectedProduct.setValue(selectedProduct);
            }

            const formsWithSameSsn =
                await RootStore.agentPortalStore.contactsFormStore.getSsnFormsAssociatedWithCurrentFormSsn();
            const duplicateSsnForms = formsWithSameSsn.filter((form) => form.formId !== parameterStore.formid);

            if (duplicateSsnForms.length > 0) {
                setDuplicateSsnForms(duplicateSsnForms);
                if (!(await confirmSave())) {
                    setLoading(false);
                    return;
                }
            }

            const form = await contactsFormStore.saveForm(formType);
            const newPath = generatePath('/agent/form/:email/:formId/:tabIndex', {
                email: form?.contact,
                formId: form?.formid,
                tabIndex: match.params.tabIndex,
            });
            window.history.replaceState(null, '', newPath);
            history.push(newPath);
            RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
            setLoading(false);
        };
        const walkthrough = parameterStore?.walkthroughData;
        const qualifiedProducts = parameterStore.qualifiedProducts;
        const recommendedProductsResponse = parameterStore.recommendedProductsResponse;
        const [iulProducts, vulProducts, termProducts, finalExpenseProducts, annuityProducts, adbProducts] = useMemo(
            () =>
                ['IUL', 'VUL', 'TERM', 'FEXP', 'ANNUITY', 'ADB'].map((type) =>
                    qualifiedProducts?.filter((rp) => rp.productTypeCode === type),
                ),
            [qualifiedProducts],
        );

        // Check if we're in Prosper Flow based on the walkthrough data
        const walkthroughRawData = (parameterStore as any)._walkthroughRawData;
        const useProsperFlow = walkthroughRawData?.userData?.useProsperFlow === true;

        // Get the recommendation type from walkthrough data - if Prosper Flow, show IUL instead of VUL
        const actualRecommendation = walkthroughRawData?.quoteData?.productTypeRecommendation;
        let recommendedProductType = useProsperFlow && actualRecommendation === 'VUL' ? 'IUL' : actualRecommendation;

        let recommendedQuote:
            | {faceAmount: number; modalPremium: number; surrenderValue?: number; termPeriod?: number; carrier?: string}
            | undefined;

        if (useProsperFlow && walkthroughRawData?.quoteData?.prosper?.quote) {
            // Use Prosper IUL quote when in Prosper Flow
            const {quote: prosperQuote, product: prosperProduct} = walkthroughRawData.quoteData.prosper;
            recommendedQuote = {
                faceAmount: prosperQuote.faceAmount,
                modalPremium: prosperQuote.modalPremium,
                surrenderValue: prosperQuote.surrenderValue || prosperQuote.cashValueAt65,
                carrier: prosperProduct.carrierName,
            };
            // Ensure we show IUL as the recommended product type
            recommendedProductType = 'IUL';
        } else if (recommendedProductType === 'IUL') {
            recommendedQuote = walkthroughRawData?.quoteData?.iul?.quote;
            if (recommendedQuote) {
                recommendedQuote.carrier = walkthroughRawData?.quoteData?.iul?.product?.carrierName;
            }
        } else if (recommendedProductType === 'TERM') {
            recommendedQuote = walkthroughRawData?.quoteData?.term?.quote;
            if (recommendedQuote) {
                recommendedQuote.carrier = walkthroughRawData?.quoteData?.term?.product?.carrierName;
            }
        } else if (recommendedProductType === 'VUL') {
            // VUL doesn't have quote in the response structure
            recommendedQuote = undefined;
        }

        const isThereEligibleProduct = (products: AllProductType[]) => {
            if (!products?.length) {
                return false;
            }
            const eligibleCodes = recommendedProductsResponse?.allEligibleProductCodes || [];
            const filteredProducts = filterQualifiedProducts(products);
            for (const product of filteredProducts) {
                // Check if product is in the eligible product codes list
                if (eligibleCodes.includes(product.code)) {
                    return true;
                }
            }
            return false;
        };

        // Separate useEffect to handle saving recommendations
        const lastSavedRecommendation = useRef<string>('');
        const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const primaryRecommendationValue = useMemo(() => {
            return prequal?.recommendedProductPrimary?.value ?? null;
        }, [prequal?.recommendedProductPrimary?.value]);

        const fallbackRecommendationValue = useMemo(() => {
            return prequal?.recommendedProductFallback?.value ?? null;
        }, [prequal?.recommendedProductFallback?.value]);

        useEffect(() => {
            // Only save if we have recommendations
            if (!primaryRecommendationValue) return;
            const email = parameterStore?.contact || parameterStore?.walkthroughData?.email;
            const customerId = parameterStore?.formid || '';
            // Create a unique key for this recommendation
            const recommendationKey = `${primaryRecommendationValue}-${fallbackRecommendationValue || ''}`;
            // Skip if same as last saved
            if (recommendationKey === lastSavedRecommendation.current) {
                console.log('Skipping save - recommendation unchanged');
                return;
            }
            console.log('Recommendation changed, will save after debounce:', {
                email,
                customerId,
                primary: primaryRecommendationValue,
                fallback: fallbackRecommendationValue,
            });
            // Clear existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Debounce the save
            saveTimeoutRef.current = setTimeout(() => {
                if (!email) {
                    console.log('Not saving - no email');
                    return;
                }
                console.log('Saving recommendations after debounce');
                const prequalParameters = {
                    age: prequal.age?.value,
                    gender: prequal.gender?.value,
                    state: prequal.state?.value,
                    tobacco: prequal.tobacco?.value,
                    height: prequal.height?.value,
                    weight: prequal.weight?.value,
                    incomeHouseholdText: prequal.incomeHouseholdText?.value,
                    coverageAmount: prequal.recommendedCoverageAmount?.value,
                    monthlyBudget: prequal.monthlyBudget?.value,
                    intentFocus: prequal.intentFocus?.value,
                    intentFunding: prequal.intentFunding?.value,
                    intentBenefitStart: prequal.intentBenefitStart?.value,
                };
                savePrequalRecommendations({
                    email,
                    customerId,
                    primaryRecommendation: primaryRecommendationValue,
                    primaryScript: prequal.recommendedProductPrimaryScript?.value,
                    fallbackRecommendation: fallbackRecommendationValue || '',
                    fallbackScript: prequal.recommendedProductFallbackScript?.value,
                    fallbackTriggers: prequal.recommendedProductFallbackTriggers?.value,
                    ineligibleForOriginal:
                        prequal.recommendedProductIneligibleForOriginalRecommendations?.value === 'true',
                    prequalParameters,
                    intentFocus: prequal.intentFocus?.value,
                    householdIncome: prequal.incomeHouseholdText?.value
                        ? Number(prequal.incomeHouseholdText?.value)
                        : undefined,
                    age: prequal.age?.value,
                });
                lastSavedRecommendation.current = recommendationKey;
            }, 3000);
            // Cleanup
            return () => {
                if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                }
            };
        }, [primaryRecommendationValue, fallbackRecommendationValue]);

        const intentFocusValue = useMemo(() => prequal?.intentFocus?.value ?? '', [prequal?.intentFocus?.value]);
        const intentFundingValue = useMemo(() => prequal?.intentFunding?.value ?? '', [prequal?.intentFunding?.value]);
        const intentBenefitStartValue = useMemo(
            () => prequal?.intentBenefitStart?.value ?? '',
            [prequal?.intentBenefitStart?.value],
        );
        const ageValue = useMemo(() => prequal?.age?.value ?? 0, [prequal?.age?.value]);
        const incomeHouseholdTextValue = useMemo(
            () => prequal?.incomeHouseholdText?.value ?? '',
            [prequal?.incomeHouseholdText?.value],
        );
        const recommendedCoverageAmountValue = useMemo(
            () => prequal?.recommendedCoverageAmount?.value ?? '',
            [prequal?.recommendedCoverageAmount?.value],
        );

        useEffect(() => {
            // Use the response properties directly to determine eligibility
            const eligibleIulProduct = isThereEligibleProduct(iulProducts);
            const eligibleTermProduct = isThereEligibleProduct(termProducts);
            const eligibleVulProduct = isThereEligibleProduct(vulProducts);
            const eligibleFinalExpenseProduct = isThereEligibleProduct(finalExpenseProducts);
            const eligibleAnnuityProduct = isThereEligibleProduct(annuityProducts);
            const eligibleAdbProduct = isThereEligibleProduct(adbProducts);

            const recommendationEligible = (rec: string) => {
                switch (rec) {
                    case 'IUL':
                        return eligibleIulProduct;
                    case 'Term':
                        return eligibleTermProduct;
                    case 'VUL':
                        return eligibleVulProduct;
                    case 'Whole Life':
                        return eligibleFinalExpenseProduct;
                    case 'Annuity':
                        return eligibleAnnuityProduct;
                    case 'ADB':
                        return eligibleAdbProduct;
                    default:
                        return false;
                }
            };

            /* productScriptPrimary, productScriptFallback, and getProductFallbackTriggers functions
               are now imported from productConstants */

            const setRecommendationsHelper = (
                recOne: string,
                recTwo: string,
                fallbackTriggers: string,
                setFallbackTriggerRegardless: boolean = false,
            ): boolean => {
                console.log('rec two ' + recTwo);
                if (recommendationEligible(recOne) && recommendationEligible(recTwo)) {
                    prequal.recommendedProductPrimary.setValue(recOne);
                    prequal.recommendedProductFallback.setValue(recTwo);
                    prequal.recommendedProductFallbackTriggers.setValue(getProductFallbackTriggers(recTwo));
                    prequal.recommendedProductPrimaryScript.setValue(productScriptPrimary(recOne));
                    prequal.recommendedProductFallbackScript.setValue(productScriptFallback(recTwo));
                    return true;
                }
                if (recommendationEligible(recOne)) {
                    prequal.recommendedProductPrimary.setValue(recOne);
                    prequal.recommendedProductPrimaryScript.setValue(productScriptPrimary(recOne));
                    if (setFallbackTriggerRegardless) {
                        prequal.recommendedProductFallbackTriggers.setValue(fallbackTriggers);
                    }
                    return true;
                }
                if (recommendationEligible(recTwo)) {
                    prequal.recommendedProductPrimary.setValue(recTwo);
                    prequal.recommendedProductPrimaryScript.setValue(productScriptPrimary(recTwo));
                    if (setFallbackTriggerRegardless) {
                        prequal.recommendedProductFallbackTriggers.setValue(fallbackTriggers);
                    }
                    return true;
                }
                return false;
            };

            const resetRecommendations = () => {
                prequal.recommendedProductPrimary.setValue('');
                prequal.recommendedProductPrimaryScript.setValue('');
                prequal.recommendedProductFallback.setValue('');
                prequal.recommendedProductFallbackTriggers.setValue('');
                prequal.recommendedProductFallbackScript.setValue('');
                prequal.recommendedProductIneligibleForOriginalRecommendations.setValue('');
            };

            const setRecommendations = (recOne: string, recTwo: string, fallbackTriggers: string = '') => {
                // Determine what the actual recommendations will be (including fallbacks)
                let actualPrimary = '';
                let actualFallback = '';
                let willBeIneligible = false;

                // Check if the original recommendations are eligible
                if (recommendationEligible(recOne) && recommendationEligible(recTwo)) {
                    actualPrimary = recOne;
                    actualFallback = recTwo;
                } else if (recommendationEligible(recOne)) {
                    actualPrimary = recOne;
                } else if (recommendationEligible(recTwo)) {
                    actualPrimary = recTwo;
                } else {
                    // Neither are eligible, will use fallback logic
                    willBeIneligible = true;
                    if (['PROTECTION', 'INHERITANCE'].includes(prequal.intentFocus.value)) {
                        if (recommendationEligible('ADB')) {
                            actualPrimary = 'ADB';
                            if (recommendationEligible('Annuity')) {
                                actualFallback = 'Annuity';
                            }
                        } else if (recommendationEligible('Annuity')) {
                            actualPrimary = 'Annuity';
                        }
                    } else if (['RETIREMENT', 'SAVINGS'].includes(prequal.intentFocus.value)) {
                        if (recommendationEligible('Annuity')) {
                            actualPrimary = 'Annuity';
                            if (recommendationEligible('ADB')) {
                                actualFallback = 'ADB';
                            }
                        } else if (recommendationEligible('ADB')) {
                            actualPrimary = 'ADB';
                        }
                    }
                }

                // Check if we're already set to these actual recommendations to avoid infinite loop
                if (
                    prequal.recommendedProductPrimary.value === actualPrimary &&
                    prequal.recommendedProductFallback.value === actualFallback &&
                    prequal.recommendedProductIneligibleForOriginalRecommendations.value ===
                        (willBeIneligible ? 'true' : '')
                ) {
                    return;
                }

                // reset all fields
                resetRecommendations();

                if (!setRecommendationsHelper(recOne, recTwo, fallbackTriggers)) {
                    // this means neither recommendations were eligible
                    // https://beamplify.slack.com/archives/C06G89L5HRT/p1764795597889539?thread_ts=1764794874.681799&cid=C06G89L5HRT
                    prequal.recommendedProductIneligibleForOriginalRecommendations.setValue('true');
                    if (['PROTECTION', 'INHERITANCE'].includes(prequal.intentFocus.value)) {
                        setRecommendationsHelper(
                            'ADB',
                            'Annuity',
                            'Note: The customer was ineligible for ' + recOne + ' and ' + recTwo,
                            true,
                        );
                    } else if (['RETIREMENT', 'SAVINGS'].includes(prequal.intentFocus.value)) {
                        setRecommendationsHelper(
                            'Annuity',
                            'ADB',
                            'Note: The customer was ineligible for ' + recOne + ' and ' + recTwo,
                            true,
                        );
                    }
                }
            };

            var householdIncomeCategory = 'HHI_LTE35';
            if (prequal.incomeHouseholdText.value && !isNaN(prequal.incomeHouseholdText.value)) {
                const hhi = Number(prequal.incomeHouseholdText.value);
                if (hhi > 35000) {
                    householdIncomeCategory = 'HHI_GT35';
                }
            }
            var ageCategory = '';
            if (prequal.age.value >= 20 && prequal.age.value <= 50) {
                ageCategory = 'AGE_20_50';
            } else if (prequal.age.value > 50) {
                ageCategory = 'AGE_50_PLUS';
            }

            const coverageAmount =
                prequal.recommendedCoverageAmount.value && !isNaN(prequal.recommendedCoverageAmount.value)
                    ? Number(prequal.recommendedCoverageAmount.value)
                    : 0;

            const evaluateRules = ({
                focusCategories,
                age,
                income,
                coverage,
                fundingPlan,
            }: {
                focusCategories: string[];
                age: string;
                income: string;
                coverage?: boolean;
                fundingPlan?: string[];
            }) => {
                if (
                    focusCategories.includes(prequal.intentFocus.value) &&
                    ageCategory === age &&
                    householdIncomeCategory === income
                ) {
                    if (coverage !== undefined && !coverage) {
                        return false;
                    }
                    if (fundingPlan !== undefined && !fundingPlan.includes(prequal.intentFunding.value)) {
                        return false;
                    }
                    return true;
                }

                return false;
            };

            // new logic https://docs.google.com/spreadsheets/d/1Y0J8lw8DGU59By6YRpT6TL_17mFcdxdydf55s_sp5RA/edit?gid=314196124#gid=314196124
            // if no product is eligible, then we should show the ADB recommendation
            if (
                !eligibleIulProduct &&
                !eligibleVulProduct &&
                !eligibleTermProduct &&
                !eligibleFinalExpenseProduct &&
                !eligibleAnnuityProduct &&
                !eligibleAdbProduct
            ) {
                resetRecommendations();
                prequal.recommendedProductFallbackTriggers.setValue(
                    'No product eligible or recommendations still loading.',
                );
                return;
            }

            // row 2
            if (evaluateRules({focusCategories: ['PROTECTION'], age: 'AGE_20_50', income: 'HHI_LTE35'})) {
                setRecommendations('Term', 'ADB');
                return;
            }
            // row 3
            if (evaluateRules({focusCategories: ['PROTECTION'], age: 'AGE_20_50', income: 'HHI_GT35'})) {
                setRecommendations('IUL', 'Term');
                return;
            }
            // row 4
            if (evaluateRules({focusCategories: ['PROTECTION'], age: 'AGE_50_PLUS', income: 'HHI_LTE35'})) {
                setRecommendations('Term', 'Whole Life');
                return;
            }
            // row 5
            if (
                evaluateRules({
                    focusCategories: ['PROTECTION'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    coverage: coverageAmount < 100000,
                })
            ) {
                setRecommendations('IUL', 'Whole Life');
                return;
            }
            // row 6
            if (
                evaluateRules({
                    focusCategories: ['PROTECTION'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    coverage: coverageAmount >= 100000,
                })
            ) {
                setRecommendations('IUL', 'Term');
                return;
            }
            // row 7
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_20_50',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 8
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_20_50',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 9
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_20_50',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 10
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_20_50',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 11
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_20_50',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 12
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_20_50',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 13
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_20_50',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 14
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_20_50',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 15
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('Annuity', 'Whole Life');
                return;
            }
            // row 16
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 17
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 18
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 19
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 20
            if (
                evaluateRules({
                    focusCategories: ['RETIREMENT'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 21
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('Annuity', 'IUL');
                return;
            }
            // row 22
            if (
                evaluateRules({
                    focusCategories: ['SAVINGS'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('IUL', 'Annuity');
                return;
            }
            // row 23
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_20_50',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Whole Life');
                return;
            }
            // row 24
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_20_50',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Whole Life');
                return;
            }
            // row 25
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_20_50',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('Whole Life', 'Term');
                return;
            }
            // row 26
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_20_50',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('IUL', 'Whole Life');
                return;
            }
            // row 27
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('Whole Life', 'Term');
                return;
            }
            // row 28
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_LUMP'],
                })
            ) {
                setRecommendations('IUL', 'Whole Life');
                return;
            }
            // row 29
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_LTE35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('Whole Life', 'Term');
                return;
            }
            // row 30
            if (
                evaluateRules({
                    focusCategories: ['INHERITANCE'],
                    age: 'AGE_50_PLUS',
                    income: 'HHI_GT35',
                    fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
                })
            ) {
                setRecommendations('IUL', 'Whole Life');
                return;
            }

            setRecommendations('IUL', 'Term', 'Default recommendation. Please notify management to review this case.');
        }, [
            intentFocusValue,
            intentFundingValue,
            intentBenefitStartValue,
            ageValue,
            incomeHouseholdTextValue,
            recommendedCoverageAmountValue,
            iulProducts,
            vulProducts,
            termProducts,
            finalExpenseProducts,
            annuityProducts,
            adbProducts,
        ]);

        if (parameterStore === null) return null;

        const coverageAmount = recommendedQuote?.faceAmount;
        const monthlyPremium = recommendedQuote?.modalPremium;
        const hasRecommendation = coverageAmount && monthlyPremium && parameterStore.walkthroughData.hasSelectedProduct;
        const investmentGoals = parameterStore.walkthroughData?.investmentGoals || [];
        let clientProgressStage = 0;
        if (hasRecommendation) clientProgressStage = 3;
        else if (walkthrough.recommended) clientProgressStage = 2;
        else if (walkthrough) clientProgressStage = 1;

        const classes = useStyles();

        return (
            <Grid container spacing={2}>
                <Grid item xs={8} sm={9} md={9}>
                    <Spacing height={20} />
                    <ClientProgress stage={clientProgressStage} />
                    <Card style={{padding: 20, marginBottom: 20}}>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <span style={{height: 1, backgroundColor: 'red', flex: 1}} />
                            <p style={{color: 'red', fontWeight: 500, textAlign: 'center', padding: 18}}>
                                we&apos;re on a recorded line
                            </p>
                            <span style={{height: 1, backgroundColor: 'red', flex: 1}} />
                        </div>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <Typography
                                    gutterBottom
                                    style={{color: '#838383', textTransform: 'capitalize', fontWeight: 'bold'}}
                                >
                                    Client Goals
                                </Typography>
                                <ClientGoals investmentGoals={investmentGoals} />
                            </Grid>
                        </Grid>

                        <CardContent>
                            <div>
                                <strong className={classes.recommendationTitle}>Questions</strong>
                            </div>
                            <Grid container>
                                <Grid item xs={8}>
                                    <Typography>
                                        If you had to choose one, would you say your focus is more on saving for long
                                        term, getting retirement income in the next 15 years, maximizing life insurance
                                        protection right now, or passing down an inheritance?
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <SelectInputElement2
                                        param={{
                                            label: 'Main Focus',
                                            options: fieldOptions.IntentFocus.options,
                                            optionValues: fieldOptions.IntentFocus.optionValues,
                                            ...prequal.intentFocus,
                                            name: 'intentFocus',
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={8}>
                                    <Typography>
                                        When you picture funding a plan like this, would you prefer a one-time lump sum,
                                        monthly contributions, or a mix of both?
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <SelectInputElement2
                                        param={{
                                            label: 'Funding Plan',
                                            options: fieldOptions.IntentFunding.options,
                                            optionValues: fieldOptions.IntentFunding.optionValues,
                                            ...prequal.intentFunding,
                                            name: 'intentFunding',
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>

                        <BasicInfo parameters={prequal} valueChanged={valueChanged} />
                        <Finances walkthroughData={walkthroughData} parameters={prequal} valueChanged={valueChanged} />
                        <HealthInfo parameters={prequal} healthParameters={health} valueChanged={valueChanged} />
                        <Behavior parameters={prequal} valueChanged={valueChanged} />

                        {historyFormId && (
                            <Grid xs={12} md={3}>
                                <MUIButton
                                    disabled={loading}
                                    variant="outlined"
                                    color="primary"
                                    style={{marginTop: 12, width: '100%'}}
                                    onClick={async () => {
                                        parameterStore.formid = '';
                                        saveForm();
                                    }}
                                >
                                    Save as a form
                                </MUIButton>
                            </Grid>
                        )}
                        <BestFitProduct
                            parameterStore={parameterStore}
                            customerQuoteData={customerQuoteData}
                            onSave={saveForm}
                            loading={loading}
                        />
                    </Card>
                </Grid>
                <Grid item xs={4} sm={3} md={3}>
                    <AgentAssistPanel
                        onValueChangedNotes={(value) => valueChanged({...prequal?.notes, name: 'notes'}, value)}
                    />
                </Grid>
                <Dialog
                    open={saveConfirmationOpen}
                    onClose={() => cancelPendingSave()}
                    aria-labelledby="confirm-dialog-title"
                >
                    <DialogTitle id="confirm-dialog-title">Confirmation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>The SSN is associated with following forms:</DialogContentText>
                        <DialogContentText>
                            <ul>
                                {duplicateSsnForms.map(({formId, email}) => (
                                    <li>
                                        {formId} - {email}
                                    </li>
                                ))}
                            </ul>
                        </DialogContentText>
                        <DialogContentText>Please double check before confirming save.</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => cancelPendingSave()} color="primary">
                            Cancel
                        </Button>
                        <Button color="primary" onClick={() => confirmPendingSave()}>
                            Confirm Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        );
    },
);
